import { InjectionToken, Signal } from '@angular/core';
import { DateTime } from 'luxon';
import { z } from 'zod';

export const TL_SNAPSHOT_URL = new InjectionToken<string>('TL_SNAPSHOT_URL');
export const TRENDLINES_TABLE_DOC = 'trendlines_table_doc';
export const TRENDLINES_CACHE_DB = 'trendlines-cache';

export const RowKeysSchema = z.union([
    z.literal('SpotLoadPosts'),
    z.literal('SpotTruckPosts'),
    z.literal('VanLoadToTruck'),
    z.literal('VanSpotRates'),
    z.literal('FlatbedLoadToTruck'),
    z.literal('FlatbedSpotRates'),
    z.literal('ReeferLoadToTruck'),
    z.literal('ReeferSpotRates'),
    z.literal('FuelPrices'),
]);

export const ColumnKeysSchema = z.union([
    z.literal('Trends'),
    z.literal('WeekComparison'),
    z.literal('MonthComparison'),
    z.literal('YearComparison'),
]);

export const UpsellColumnKeysSchema = z.union([
    ColumnKeysSchema,
    z.literal('EightDayForecastUpsell'),
    z.literal('ThirtyFiveDayForecastUpsell'),
]);

export type RowKeys = z.infer<typeof RowKeysSchema>;
export type ColumnKeys = z.infer<typeof ColumnKeysSchema>;
export type UpsellColumnKeys = z.infer<typeof UpsellColumnKeysSchema>;

export const EquipmentTypeSchema = z.union([
    z.literal('VAN'),
    z.literal('REEFER'),
    z.literal('FLATBED'),
]);

export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

export const ColSchema = z.object({
    id: z.string(),
    label: z.string(),
    isLink: z.boolean().optional(),
    isUpsell: z.boolean().optional(),
    linkLabel: z.string(),
    redirectUrl: z.string().optional(),
});

export type Col = z.infer<typeof ColSchema>;

export const RowSchema = z.record(ColumnKeysSchema, ColSchema);
export const DataRowsSchema = z.record(ColumnKeysSchema, z.number());

export type Row = z.infer<typeof RowSchema>;

export const RowOrder: RowKeys[] = [
    'SpotLoadPosts',
    'SpotTruckPosts',
    'VanLoadToTruck',
    'VanSpotRates',
    'FlatbedLoadToTruck',
    'FlatbedSpotRates',
    'ReeferLoadToTruck',
    'ReeferSpotRates',
    'FuelPrices',
];

export type DateSpanSchema = {
    start: DateTime;
    end: DateTime;
};

export type WeekSpanSchema = {
    week1: DateSpanSchema;
    week2: DateSpanSchema;
};

export const TrendlinesLabelCol: Record<RowKeys, Col> = {
    SpotLoadPosts: {
        id: 'spot-load-posts',
        label: 'Spot Load Posts',
    },
    SpotTruckPosts: {
        id: 'spot-truck-posts',
        label: 'Spot Truck Posts',
    },
    VanLoadToTruck: {
        id: 'van-load-to-truck',
        linkLabel: 'Van Load to Truck',
        isLink: true,
        redirectUrl: '/trendlines/van/demand-and-capacity',
    },
    VanSpotRates: {
        id: 'van-spot-rates',
        linkLabel: `Van Spot Rates`,
        isLink: true,
        redirectUrl: '/trendlines/van/national-rates',
    },
    FlatbedLoadToTruck: {
        id: 'flatbed-load-to-truck',
        linkLabel: 'Flatbed Load to Truck',
        isLink: true,
        redirectUrl: '/trendlines/flatbed/demand-and-capacity',
    },
    FlatbedSpotRates: {
        id: 'flatbed-spot-rates',
        linkLabel: 'Flatbed Spot Rates',
        isLink: true,
        redirectUrl: '/trendlines/flatbed/national-rates',
    },
    ReeferLoadToTruck: {
        id: 'reefer-load-to-truck',
        linkLabel: 'Reefer Load to Truck',
        isLink: true,
        redirectUrl: '/trendlines/reefer/demand-and-capacity',
    },
    ReeferSpotRates: {
        id: 'reefer-spot-rates',
        linkLabel: 'Reefer Spot Rates',
        isLink: true,
        redirectUrl: '/trendlines/reefer/national-rates',
    },
    FuelPrices: {
        id: 'fuel-prices',
        label: '**Fuel Prices**',
    },
};

export const getWeekSpan = (): WeekSpanSchema => {
    const datetime = DateTime.now().toUTC();
    let addExtraWeek = 0;
    if (datetime.weekday === 1) {
        if (datetime.hour < 12) {
            //12 because the Job runs at 11:30 UTC extra half hour for processing
            addExtraWeek = 1;
        }
    }
    return {
        week1: {
            start: datetime.minus({ week: 1 + addExtraWeek }).startOf('week'),
            end: datetime
                .minus({ week: 0 + addExtraWeek })
                .startOf('week')
                .minus({ days: 1 }),
        },
        week2: {
            start: datetime.minus({ week: 2 + addExtraWeek }).startOf('week'),
            end: datetime
                .minus({ week: 1 + addExtraWeek })
                .startOf('week')
                .minus({ days: 1 }),
        },
    };
};

export const getWeekLabel = () => {
    const weekSpan: WeekSpanSchema = getWeekSpan();
    const startString: string = `${weekSpan.week1.start.toFormat(
        'MMM dd',
    )} - ${weekSpan.week1.end.toFormat('MMM dd')}`.toUpperCase();
    const endString: string = `${weekSpan.week2.start.toFormat(
        'MMM dd',
    )} - ${weekSpan.week2.end.toFormat('MMM dd')}`.toUpperCase();

    return `WEEK <br><span class="!text-[10px]"> **${startString} VS ${endString}** </span>`;
};

export const getCardWeekLabel = () => {
    const weekSpan: WeekSpanSchema = getWeekSpan();
    const startString: string = `${weekSpan.week1.start.toFormat(
        'MMM dd',
    )} - ${weekSpan.week1.end.toFormat('MMM dd')}`;
    const endString: string = `${weekSpan.week2.start.toFormat(
        'MMM dd',
    )} - ${weekSpan.week2.end.toFormat('MMM dd')}`;

    return `WEEK (${startString} VS ${endString})`.toUpperCase();
};

export const getYearSpan = (): DateSpanSchema => {
    const currentDate = DateTime.now();
    return {
        start: currentDate
            .minus({ month: 1 })
            .minus({ year: 0 })
            .startOf('month'),
        end: currentDate
            .minus({ month: 1 })
            .minus({ year: 1 })
            .startOf('month'),
    };
};

export const getMonthSpan = (): DateSpanSchema => {
    const currentDate = DateTime.now();
    return {
        start: currentDate.minus({ month: 1 }).startOf('month'),
        end: currentDate.minus({ month: 2 }).startOf('month'),
    };
};

export const getMonthLabel = () => {
    const monthSpan: DateSpanSchema = getMonthSpan();
    const monthStart: string = monthSpan.start
        .toFormat('MMM yyyy')
        .toString()
        .toUpperCase();
    const monthEnd: string = monthSpan.end
        .toFormat('MMM yyyy')
        .toString()
        .toUpperCase();
    return `MONTH <br> <span class="!text-[10px]">**${monthStart} VS ${monthEnd}** </span>`;
};
export const getCardMonthLabel = () => {
    const monthSpan: DateSpanSchema = getMonthSpan();

    return `MONTH (${monthSpan.start.toFormat(
        'MMM yyyy',
    )} VS ${monthSpan.end.toFormat('MMM yyyy')})`.toUpperCase();
};

export const getYearLabel = () => {
    const yearSpan: DateSpanSchema = getYearSpan();
    const yearStart: string = yearSpan.start
        .toFormat('MMM yyyy')
        .toString()
        .toUpperCase();
    const yearEnd: string = yearSpan.end
        .toFormat('MMM yyyy')
        .toString()
        .toUpperCase();
    return `YEAR <br> <span class="!text-[10px]">**${yearStart} VS ${yearEnd}**</span>`;
};

export const getCardYearLabel = () => {
    const yearSpan: DateSpanSchema = getYearSpan();

    return `YEAR (${yearSpan.start.toFormat(
        'MMM yyyy',
    )} VS ${yearSpan.end.toFormat('MMM yyyy')})`.toUpperCase();
};

export const getEightDayForecastUpsellLabel = () => {
    const firstDayOfCurrentWeek = DateTime.now().startOf('week');
    const eightDayForecastUpsell = firstDayOfCurrentWeek.plus({ days: 8 });
    return `8 Day Forecast <br> <span class="!text-[10px]">**${firstDayOfCurrentWeek
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()} VS ${eightDayForecastUpsell
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()}**</span>`;
};

export const getEightDayForecastUpsellCardLabel = () => {
    const eightDayForecastUpsell = DateTime.now()
        .startOf('week')
        .plus({ days: 8 });
    return `8 Day Forecast (${eightDayForecastUpsell
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()})`;
};

export const getThirtyFiveDayForecastUpsellLabel = () => {
    const firstDayOfCurrentWeek = DateTime.now().startOf('week');
    const thirtyFiveDayForecastUpsell = firstDayOfCurrentWeek.plus({
        days: 35,
    });
    return `35 Day Forecast <br> <span class="!text-[10px]">**${firstDayOfCurrentWeek
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()} VS ${thirtyFiveDayForecastUpsell
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()}**</span>`;
};

export const getThirtyFiveDayForecastUpsellCardLabel = () => {
    const thirtyFiveDayForecastUpsell = DateTime.now()
        .startOf('week')
        .plus({ days: 35 });
    return `35 Day Forecast (${thirtyFiveDayForecastUpsell
        .toFormat('MMM dd')
        .toString()
        .toUpperCase()})`;
};

export const ColumnOrder: ColumnKeys[] = [
    'Trends',
    'WeekComparison',
    'MonthComparison',
    'YearComparison',
];

export const UpsellColumnOrder: UpsellColumnKeys[] = [...ColumnOrder, 'EightDayForecastUpsell', 'ThirtyFiveDayForecastUpsell'];

export interface IIqTrendlinesSnapshotStateService {
    rows(
        response: TrendlinesSnapshotResponse,
        isUpsellEnabled: boolean,
    ): Signal<Rows> | Signal<UpsellRows>;
    data(): Promise<DataRows>;
}

export type Rows = Record<RowKeys, Record<ColumnKeys , Col>>;
export type UpsellRows = Record<RowKeys, Record<UpsellColumnKeys, Col>>;
export type DataRows = Record<RowKeys, Record<ColumnKeys , number | string>>;
export type UpsellDataRows = Record<RowKeys, Record<UpsellColumnKeys, number | string>>;

export type TrendlinesSnapshotResponse = {
    vanTrends: VanTrends;
    reeferTrends: ReeferTrends;
    flatbedTrends: FlatbedTrends;
    nationalTrends: NationalTrends;
};


export type VanTrends = {
    weekOverWeekVanLoadToTruckRatioChangeInPercentage: string | number;
    monthOverMonthVanLoadToTruckRatioChangeInPercentage: string | number;
    yearOverYearVanLoadToTruckRatioChangeInPercentage: string | number;
    weekOverWeekVanSpotRateChangeInPercentage: string | number;
    monthOverMonthVanSpotRateChangeInPercentage: string | number;
    yearOverYearVanSpotRateChangeInPercentage: string | number;
};
export type ReeferTrends = {
    weekOverWeekReeferLoadToTruckRatioChangeInPercentage: string | number;
    monthOverMonthReeferLoadToTruckRatioChangeInPercentage: string | number;
    yearOverYearReeferLoadToTruckRatioChangeInPercentage: string | number;
    weekOverWeekReeferSpotRateChangeInPercentage: string | number;
    monthOverMonthReeferSpotRateChangeInPercentage: string | number;
    yearOverYearReeferSpotRateChangeInPercentage: string | number;
};
export type FlatbedTrends = {
    weekOverWeekFlatbedLoadToTruckRatioChangeInPercentage: string | number;
    monthOverMonthFlatbedLoadToTruckRatioChangeInPercentage: string | number;
    yearOverYearFlatbedLoadToTruckRatioChangeInPercentage: string | number;
    weekOverWeekFlatbedSpotRateChangeInPercentage: string | number;
    monthOverMonthFlatbedSpotRateChangeInPercentage: string | number;
    yearOverYearFlatbedSpotRateChangeInPercentage: string | number;
};
export type NationalTrends = {
    weekOverWeekNationalLoadPostingsChangeInPercentage: string | number;
    weekOverWeekNationalTruckPostingsChangeInPercentage: string | number;
    monthOverMonthNationalLoadPostingsChangeInPercentage: string | number;
    monthOverMonthNationalTruckPostingsChangeInPercentage: string | number;
    yearOverYearNationalLoadPostingsChangeInPercentage: string | number;
    yearOverYearNationalTruckPostingsChangeInPercentage: string | number;
    weekOverWeekNationalFuelPriceChangeInPercentage: string | number;
    monthOverMonthNationalFuelPriceChangeInPercentage: string | number;
    yearOverYearNationalFuelPriceChangeInPercentage: string | number;
};

export const emptyDataRow: Record<ColumnKeys | UpsellColumnKeys, number | string> = {
    Trends: 0,
    WeekComparison: 0,
    MonthComparison: 0,
    YearComparison: 0,
    EightDayForecastUpsell: '--',
    ThirtyFiveDayForecastUpsell: '--',
};
export const emptyDataRows: DataRows = {
    SpotLoadPosts: emptyDataRow,
    SpotTruckPosts: emptyDataRow,
    VanLoadToTruck: emptyDataRow,
    VanSpotRates: emptyDataRow,
    FlatbedLoadToTruck: emptyDataRow,
    FlatbedSpotRates: emptyDataRow,
    ReeferLoadToTruck: emptyDataRow,
    ReeferSpotRates: emptyDataRow,
    FuelPrices: emptyDataRow,
};

export const emptyRow: Record<ColumnKeys | UpsellColumnKeys, Col> = {
    Trends: undefined,
    WeekComparison: undefined,
    MonthComparison: undefined,
    YearComparison: undefined,
    EightDayForecastUpsell: undefined,
    ThirtyFiveDayForecastUpsell: undefined,
};
export const emptyRows: Rows = {
    SpotLoadPosts: emptyRow,
    SpotTruckPosts: emptyRow,
    VanLoadToTruck: emptyRow,
    VanSpotRates: emptyRow,
    FlatbedLoadToTruck: emptyRow,
    FlatbedSpotRates: emptyRow,
    ReeferLoadToTruck: emptyRow,
    ReeferSpotRates: emptyRow,
    FuelPrices: emptyRow,
};

export const defaultTrendlinesSnapshotResponse: TrendlinesSnapshotResponse = {
    vanTrends: {
        weekOverWeekVanLoadToTruckRatioChangeInPercentage: '--',
        monthOverMonthVanLoadToTruckRatioChangeInPercentage: '--',
        yearOverYearVanLoadToTruckRatioChangeInPercentage: '--',
        weekOverWeekVanSpotRateChangeInPercentage: '--',
        monthOverMonthVanSpotRateChangeInPercentage: '--',
        yearOverYearVanSpotRateChangeInPercentage: '--',
    },
    reeferTrends: {
        weekOverWeekReeferLoadToTruckRatioChangeInPercentage: '--',
        monthOverMonthReeferLoadToTruckRatioChangeInPercentage: '--',
        yearOverYearReeferLoadToTruckRatioChangeInPercentage: '--',
        weekOverWeekReeferSpotRateChangeInPercentage: '--',
        monthOverMonthReeferSpotRateChangeInPercentage: '--',
        yearOverYearReeferSpotRateChangeInPercentage: '--',
    },
    flatbedTrends: {
        weekOverWeekFlatbedLoadToTruckRatioChangeInPercentage: '--',
        monthOverMonthFlatbedLoadToTruckRatioChangeInPercentage: '--',
        yearOverYearFlatbedLoadToTruckRatioChangeInPercentage: '--',
        weekOverWeekFlatbedSpotRateChangeInPercentage: '--',
        monthOverMonthFlatbedSpotRateChangeInPercentage: '--',
        yearOverYearFlatbedSpotRateChangeInPercentage: '--',
    },
    nationalTrends: {
        weekOverWeekNationalLoadPostingsChangeInPercentage: '--',
        weekOverWeekNationalTruckPostingsChangeInPercentage: '--',
        monthOverMonthNationalLoadPostingsChangeInPercentage: '--',
        monthOverMonthNationalTruckPostingsChangeInPercentage: '--',
        yearOverYearNationalLoadPostingsChangeInPercentage: '--',
        yearOverYearNationalTruckPostingsChangeInPercentage: '--',
        weekOverWeekNationalFuelPriceChangeInPercentage: '--',
        monthOverMonthNationalFuelPriceChangeInPercentage: '--',
        yearOverYearNationalFuelPriceChangeInPercentage: '--',
    },
};

export const UpsellTrackingEventName = 'Clicked To Learn More - iQ Upsell';
